import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCsrfToken } from '../../Hooks/useCsrfToken';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

function LeagueStandingsCard({ rosterId, get_league_standings_endpoint }) {
	const { user } = useAuth();
	const csrfToken = useCsrfToken();
	const [
		standings,
		setStandings
	] = useState([]);
	const [
		loading,
		setLoading
	] = useState(true);
	const [
		error,
		setError
	] = useState(null);
	const [
		hasDivisions,
		setHasDivisions
	] = useState(false);
	const [
		divisions,
		setDivisions
	] = useState([]);

	const fetchStandings = useCallback(
		async () => {
			try {
				setLoading(true);
				setError(null);

				const standingsUrl = get_league_standings_endpoint.replace(
					'roster_id',
					rosterId
				);

				const response = await fetch(standingsUrl, {
					credentials: 'same-origin',
					cache: 'no-cache',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken,
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache'
					}
				});

				const data = await response.json();

				if (data.success && data.data) {
					setStandings(data.data);
					setHasDivisions(data.has_divisions || false);
					setDivisions(data.divisions || []);
				} else {
					setError(data.message || 'Failed to load standings data');
				}
			} catch (err) {
				console.error('Error fetching standings:', err);
				setError('Failed to load standings');
			} finally {
				setLoading(false);
			}
		},
		[
			rosterId,
			csrfToken
		]
	);

	useEffect(
		() => {
			if (csrfToken) {
				fetchStandings();
			}
		},
		[
			csrfToken,
			fetchStandings
		]
	);

	// Check if we should show the streak column (hide if all streaks are "-")
	const hasStreakData = standings.some(
		(team) => team.streak && team.streak !== '-'
	);

	return (
		<div className="mt-14">
			<div className="text-3xl font-bold mb-3 text-gray-800 dark:text-dark-text">
				<h4>League Standings</h4>
			</div>
			<div className="border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card p-6">
				{loading ? (
					<div className="text-center">
						<LoadingSpinner />
					</div>
				) : (
					<React.Fragment>
						{error && (
							<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
								<p>{error}</p>
							</div>
						)}

						{standings.length === 0 ? (
							<div className="text-gray-500 text-center py-12">
								<i className="fas fa-trophy text-6xl mb-4 block opacity-30" />
								<p className="text-lg font-medium mb-2">No Standings Data</p>
								<p className="text-sm">
									Standings will load automatically when available.
								</p>
							</div>
						) : hasDivisions ? (
							<div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
								{divisions.map((division, divIndex) => {
									const divisionTeams = standings.filter(
										(t) => t.division === division
									);
									return (
										<div key={divIndex} className="mb-6 last:mb-0">
											<h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-3">
												{division}
											</h3>
											<table
												className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 mb-4"
												style={{ tableLayout: 'fixed' }}
											>
												<thead className="bg-gray-50 dark:bg-gray-800">
													<tr>
														<th
															className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
															style={{ width: '40%' }}
														>
															Team
														</th>
														<th
															className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
															style={{ width: '15%' }}
														>
															Record
														</th>
														<th
															className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
															style={{ width: '10%' }}
														>
															VP
														</th>
														<th
															className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
															style={{ width: '17.5%' }}
														>
															PF
														</th>
														<th
															className="px-2 sm:px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
															style={{ width: '17.5%' }}
														>
															PA
														</th>
													</tr>
												</thead>
												<tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
													{divisionTeams.map((team, index) => (
														<tr
															key={index}
															className="hover:bg-gray-50 dark:hover:bg-gray-800"
														>
															<td
																className="px-2 sm:px-3 py-3 text-sm text-gray-700 dark:text-gray-300"
																style={{ width: '40%' }}
															>
																<div className="flex items-center gap-2">
																	<span className="font-medium truncate">
																		{team.team_name}
																	</span>
																</div>
															</td>
															<td
																className="px-2 sm:px-3 py-3 whitespace-nowrap text-sm text-center text-gray-700 dark:text-gray-300"
																style={{ width: '15%' }}
															>
																{team.record}
															</td>
															<td
																className="px-2 sm:px-3 py-3 whitespace-nowrap text-sm text-center font-semibold text-gray-900 dark:text-gray-100"
																style={{ width: '10%' }}
															>
																{team.victory_points}
															</td>
															<td
																className="px-2 sm:px-3 py-3 whitespace-nowrap text-sm text-center text-gray-700 dark:text-gray-300"
																style={{ width: '17.5%' }}
															>
																{team.points_for.toFixed(2)}
															</td>
															<td
																className="px-2 sm:px-3 py-3 whitespace-nowrap text-sm text-center text-gray-700 dark:text-gray-300"
																style={{ width: '17.5%' }}
															>
																{team.points_against.toFixed(2)}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									);
								})}
							</div>
						) : (
							<div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
								<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
									<thead className="bg-gray-50 dark:bg-gray-800">
										<tr>
											<th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												Rank
											</th>
											<th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												Team
											</th>
											<th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												W-L-T
											</th>
											<th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												PF
											</th>
											<th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												PA
											</th>
											{hasStreakData && (
												<th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
													Streak
												</th>
											)}
										</tr>
									</thead>
									<tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
										{standings.map((team, index) => {
											return (
												<tr
													key={index}
													className="hover:bg-gray-50 dark:hover:bg-gray-800"
												>
													<td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-gray-100">
														{team.rank}
													</td>
													<td className="px-2 sm:px-4 py-4 text-sm text-gray-700 dark:text-gray-300">
														<div className="flex items-center gap-2 sm:gap-3">
															{team.team_logo_url && (
																<img
																	src={team.team_logo_url}
																	alt={`${team.team_name} logo`}
																	className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-gray-300 dark:border-gray-700 shadow-sm flex-shrink-0"
																	onError={(e) => {
																		e.target.style.display = 'none';
																	}}
																/>
															)}
															<span className="truncate font-medium">
																{team.team_name}
															</span>
														</div>
													</td>
													<td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm text-center text-gray-700 dark:text-gray-300">
														{team.record}
													</td>
													<td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm text-center text-gray-700 dark:text-gray-300">
														{team.points_for.toFixed(2)}
													</td>
													<td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm text-center text-gray-700 dark:text-gray-300">
														{team.points_against.toFixed(2)}
													</td>
													{hasStreakData && (
														<td className="px-2 sm:px-4 py-4 whitespace-nowrap text-sm text-center">
															<span
																className={`px-2 py-1 rounded text-xs font-semibold ${team.streak.startsWith(
																	'W'
																)
																	? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
																	: team.streak.startsWith('L')
																		? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
																		: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}
															>
																{team.streak}
															</span>
														</td>
													)}
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</React.Fragment>
				)}
			</div>
		</div>
	);
}

export default LeagueStandingsCard;
